/**
 * El esquema de la base de datos: qué pestañas existen, con qué columnas y de qué tipo.
 *
 * Dos decisiones de aislamiento que sostienen §2:
 *
 * 1. **Una planilla por usuario.** Sheets no tiene row-level security, así que el límite se
 *    pone donde sí existe: el documento. Los datos personales de dos usuarios están en dos
 *    archivos distintos. Un bug de filtrado no puede filtrar datos de otro usuario porque no
 *    hay una tabla común de la que filtrarlos.
 * 2. **La planilla compartida no tiene datos de salud.** Sólo el catálogo global de
 *    ejercicios, los gimnasios, el mapa de máquinas (que §2 define como activo compartido) y
 *    un índice de autenticación con lo mínimo para resolver quién es quién. El perfil
 *    —altura, sexo, fecha de nacimiento— vive en la planilla personal.
 *
 * Un detalle que parece menor y no lo es: los números se escriben con punto decimal y la
 * planilla se crea con locale en_US. Si la planilla quedara en locale español, la API
 * guardaría "97.5" como texto y el usuario vería sus pesos alineados a la izquierda,
 * inutilizables para cualquier fórmula suya. Al leer se aceptan las dos formas, porque el
 * usuario puede editar a mano y escribir "97,5".
 */

export type TipoColumna =
  | 'texto'
  | 'numero'
  | 'entero'
  | 'booleano'
  | 'fecha'
  | 'hora'
  | 'instante'
  | 'lista';

export interface Columna {
  nombre: string;
  tipo: TipoColumna;
  /** Si true, la celda vacía se lee como null en vez de como 0 o ''. */
  nulable?: boolean;
  /** Nota que se escribe como comentario de cabecera al crear la pestaña. */
  ayuda?: string;
}

export type Ambito = 'compartida' | 'personal';

export interface DefinicionTab {
  nombre: string;
  ambito: Ambito;
  columnas: Columna[];
  /** Columna que identifica la fila. Siempre 'id' salvo en pestañas de una sola fila. */
  clave: string;
  /** Columna de clave de idempotencia, si la pestaña recibe escrituras del cliente. */
  idem?: string;
  descripcion: string;
}

const col = (nombre: string, tipo: TipoColumna, nulable = false, ayuda?: string): Columna => ({
  nombre,
  tipo,
  nulable,
  ...(ayuda ? { ayuda } : {}),
});

// ---------------------------------------------------------------------------
// Planilla COMPARTIDA — sin datos de salud
// ---------------------------------------------------------------------------

export const TAB_CUENTAS: DefinicionTab = {
  nombre: 'cuentas',
  ambito: 'compartida',
  clave: 'id',
  descripcion:
    'Índice de autenticación. Lo mínimo para resolver quién es quién y en qué planilla están sus datos. NUNCA agregar acá altura, peso, sexo ni nada de salud: eso va en la planilla personal.',
  columnas: [
    col('id', 'texto'),
    col('email', 'texto'),
    col('hash_clave', 'texto', false, 'scrypt. Nunca la clave en claro.'),
    col('planilla_id', 'texto', false, 'Planilla personal de este usuario.'),
    col('creado_en', 'instante'),
  ],
};

export const TAB_GIMNASIOS: DefinicionTab = {
  nombre: 'gimnasios',
  ambito: 'compartida',
  clave: 'id',
  descripcion: 'Global: varios usuarios pueden entrenar en el mismo gimnasio.',
  columnas: [
    col('id', 'texto'),
    col('nombre', 'texto'),
    col('notas', 'texto', true, 'Texto libre, ej. "cerrado domingos".'),
    col('dias_cierre_semanal', 'lista', false, '0=domingo. Ej. "0" o "0|6".'),
  ],
};

export const TAB_EJERCICIOS: DefinicionTab = {
  nombre: 'ejercicios',
  ambito: 'compartida',
  clave: 'id',
  descripcion: 'Catálogo base global: un leg press es un leg press para todos.',
  columnas: [
    col('id', 'texto'),
    col('nombre', 'texto'),
    col('musculo_primario', 'texto'),
    col('musculos_secundarios', 'lista'),
    col('tipo', 'texto', false, 'maquina | mancuerna | barra | peso_corporal | cardio'),
    col('patron', 'texto', false, 'empuje | tiron | pierna | core | cardio'),
    col('unilateral', 'booleano'),
    col('es_global', 'booleano'),
    col('creado_por_user_id', 'texto', true),
    col('rango_reps_min', 'entero', true),
    col('rango_reps_max', 'entero', true),
  ],
};

export const TAB_MAQUINAS: DefinicionTab = {
  nombre: 'maquinas',
  ambito: 'compartida',
  clave: 'id',
  descripcion:
    'Mapa físico del gimnasio, compartido por gimnasio (§2). Si un usuario registra dónde está el leg press, los demás lo heredan. Es la única función social del producto y no expone ningún dato personal.',
  columnas: [
    col('id', 'texto'),
    col('gimnasio_id', 'texto'),
    col('etiqueta_ubicacion', 'texto', false, 'Ej. "Máquina baranda 3".'),
    col('ejercicio_id', 'texto'),
    col('unidad_placa', 'texto', false, 'lb | kg'),
    col('incremento_placa', 'numero'),
    col('placa_min', 'numero', true),
    col('placa_max', 'numero', true),
  ],
};

// ---------------------------------------------------------------------------
// Planilla PERSONAL — una por usuario
// ---------------------------------------------------------------------------

export const TAB_PERFIL: DefinicionTab = {
  nombre: 'perfil',
  ambito: 'personal',
  clave: 'id',
  descripcion: 'Una sola fila. El perfil vive acá, no en la planilla compartida.',
  columnas: [
    col('id', 'texto'),
    col('nombre', 'texto'),
    col('fecha_nacimiento', 'fecha', true),
    col('sexo', 'texto'),
    col('altura_cm', 'numero', true, 'Requerida para el FFMI (§5.3).'),
    col('unidad_preferida', 'texto'),
    col('zona_horaria', 'texto'),
    col('objetivo_series_semanales', 'texto', true, 'JSON por grupo muscular, ej. {"pierna":10}'),
    col('formula_e1rm', 'texto', true, 'epley | brzycki'),
    col('objetivo_proteina_g', 'numero', true, 'Lo pone el usuario. Vacío = sin objetivo, y la app no inventa uno.'),
    col('objetivo_kcal', 'numero', true, 'Lo pone el usuario. La app nunca calcula un objetivo (§10).'),
  ],
};

export const TAB_EJERCICIOS_PROPIOS: DefinicionTab = {
  nombre: 'ejercicios_propios',
  ambito: 'personal',
  clave: 'id',
  descripcion: 'Variantes propias del usuario (§2). Mismas columnas que el catálogo global.',
  columnas: TAB_EJERCICIOS.columnas,
};

export const TAB_PLANES: DefinicionTab = {
  nombre: 'planes',
  ambito: 'personal',
  clave: 'id',
  descripcion: 'Paquetes de sesiones con vencimiento.',
  columnas: [
    col('id', 'texto'),
    col('user_id', 'texto'),
    col('gimnasio_id', 'texto'),
    col('sesiones_totales', 'entero'),
    col('fecha_inicio', 'fecha'),
    col('fecha_vencimiento', 'fecha'),
  ],
};

export const TAB_DIAS_BLOQUEADOS: DefinicionTab = {
  nombre: 'dias_bloqueados',
  ambito: 'personal',
  clave: 'id',
  descripcion: 'Viajes, cierres e indisponibilidad. user_id vacío = afecta a todo el gimnasio.',
  columnas: [
    col('id', 'texto'),
    col('user_id', 'texto', true),
    col('gimnasio_id', 'texto'),
    col('fecha', 'fecha'),
    col('motivo', 'texto'),
  ],
};

export const TAB_RUTINAS: DefinicionTab = {
  nombre: 'rutinas',
  ambito: 'personal',
  clave: 'id',
  descripcion: 'El split del usuario. La app no prescribe rutinas.',
  columnas: [col('id', 'texto'), col('user_id', 'texto'), col('nombre', 'texto'), col('activa', 'booleano')],
};

export const TAB_RUTINA_DIAS: DefinicionTab = {
  nombre: 'rutina_dias',
  ambito: 'personal',
  clave: 'id',
  descripcion: 'Día 1, Día 2, Día 3… y su orden en la rotación.',
  columnas: [col('id', 'texto'), col('rutina_id', 'texto'), col('orden', 'entero'), col('nombre', 'texto')],
};

export const TAB_RUTINA_EJERCICIOS: DefinicionTab = {
  nombre: 'rutina_ejercicios',
  ambito: 'personal',
  clave: 'id',
  descripcion: 'Qué ejercicios tiene cada día y en qué orden.',
  columnas: [
    col('id', 'texto'),
    col('rutina_dia_id', 'texto'),
    col('ejercicio_id', 'texto'),
    col('orden', 'entero'),
    col('series_objetivo', 'entero'),
    col('reps_objetivo_min', 'entero'),
    col('reps_objetivo_max', 'entero'),
  ],
};

export const TAB_SESIONES: DefinicionTab = {
  nombre: 'sesiones',
  ambito: 'personal',
  clave: 'id',
  idem: 'id',
  descripcion: 'La fecha vive acá y no repetida en cada serie: mover una sesión de día es cambiar una celda.',
  columnas: [
    col('id', 'texto'),
    col('user_id', 'texto'),
    col('plan_id', 'texto', true),
    col('rutina_dia_id', 'texto', true),
    col('fecha', 'fecha'),
    col('estado', 'texto', false, 'planificada | en_curso | completada | omitida'),
    col('gimnasio_id', 'texto'),
    col('notas', 'texto', true),
    col('iniciada_en', 'instante', true),
    col('cerrada_en', 'instante', true),
  ],
};

export const TAB_SERIES: DefinicionTab = {
  nombre: 'series',
  ambito: 'personal',
  clave: 'id',
  idem: 'idem_key',
  descripcion:
    'Una fila por serie, escrita al momento de tocarla. completada=no con motivo_omision = omitida (se EXCLUYE de los KPIs); completada=no sin motivo = pendiente o arrastrada.',
  columnas: [
    col('id', 'texto'),
    col('sesion_id', 'texto'),
    col('ejercicio_id', 'texto'),
    col('maquina_id', 'texto', true),
    col('numero_serie', 'entero'),
    col('reps', 'entero'),
    col('peso', 'numero', false, 'Tal como lo leyó el usuario. NO convertido.'),
    col('unidad', 'texto', false, 'lb | kg'),
    col('rir', 'entero', true),
    col('completada', 'booleano'),
    col('motivo_omision', 'texto', true, 'equipo_ocupado | dolor | sin_tiempo | decision'),
    col('arrastrada_de_sesion_id', 'texto', true),
    col('registrada_en', 'instante'),
    col('idem_key', 'texto', false, 'Guardar dos veces no duplica la serie.'),
  ],
};

export const TAB_CARDIO: DefinicionTab = {
  nombre: 'cardio',
  ambito: 'personal',
  clave: 'id',
  idem: 'idem_key',
  descripcion: 'Sin nivel_resistencia el registro NO es comparable entre sesiones (§5.4).',
  columnas: [
    col('id', 'texto'),
    col('sesion_id', 'texto'),
    col('maquina_tipo', 'texto'),
    col('duracion_seg', 'entero'),
    col('distancia_km', 'numero'),
    col('calorias', 'numero'),
    col('nivel_resistencia', 'numero', true, 'Vacío = dato faltante, no cero.'),
    col('fuente', 'texto', false, 'manual | ocr'),
    col('idem_key', 'texto'),
  ],
};

export const TAB_MEDIDAS: DefinicionTab = {
  nombre: 'medidas',
  ambito: 'personal',
  clave: 'id',
  idem: 'idem_key',
  descripcion:
    'Datos de salud. La serie principal de la app es la media móvil de 7 días, no estas filas sueltas (§5.3).',
  columnas: [
    col('id', 'texto'),
    col('user_id', 'texto'),
    col('fecha', 'fecha'),
    col('hora', 'hora', false, 'La bioimpedancia se mueve con la hora del día.'),
    col('peso', 'numero'),
    col('unidad_peso', 'texto'),
    col('grasa_pct', 'numero', true),
    col('masa_muscular', 'numero', true),
    col('masa_magra', 'numero', true),
    col('masa_osea', 'numero', true),
    col('agua_pct', 'numero', true),
    col('grasa_visceral', 'numero', true),
    col('imc', 'numero', true),
    col('tmb', 'numero', true),
    col('fuente', 'texto', false, 'manual | ocr'),
    col('condiciones', 'texto', false, 'ayuno | post_entreno | desconocido'),
    col('idem_key', 'texto'),
  ],
};

export const TAB_COMIDAS: DefinicionTab = {
  nombre: 'comidas',
  ambito: 'personal',
  clave: 'id',
  idem: 'idem_key',
  descripcion:
    'Una fila por alimento, no por comida: así se corrige uno sin tocar el resto. Un día SIN filas es un día que no se anotó, no un día de cero calorías — los promedios lo excluyen.',
  columnas: [
    col('id', 'texto'),
    col('user_id', 'texto'),
    col('fecha', 'fecha'),
    col('hora', 'hora'),
    col('momento', 'texto', false, 'desayuno | almuerzo | cena | snack'),
    col('descripcion', 'texto', false, 'Con la cantidad explícita: "150 g de pechuga de pollo".'),
    col('kcal', 'numero'),
    col('proteina_g', 'numero'),
    col('carbohidratos_g', 'numero', true),
    col('grasa_g', 'numero', true),
    col('fibra_g', 'numero', true),
    col('confianza', 'numero', true, 'Certeza de la estimación del modelo. Vacío = cargado a mano.'),
    col('fuente', 'texto', false, 'manual | ia_texto | ia_foto'),
    col('idem_key', 'texto'),
  ],
};

export const TAB_INGESTAS: DefinicionTab = {
  nombre: 'ingestas_imagen',
  ambito: 'personal',
  clave: 'id',
  idem: 'id',
  descripcion:
    'Trazabilidad del OCR (§4.5.4): el JSON crudo tal como lo devolvió el modelo, para poder auditar errores después.',
  columnas: [
    col('id', 'texto'),
    col('user_id', 'texto'),
    col('tipo', 'texto', false, 'balanza | consola_cardio'),
    col('imagen_ref', 'texto', true),
    col('json_crudo', 'texto'),
    col('confianza', 'numero'),
    col('confirmado_por_usuario', 'booleano'),
    col('medida_id', 'texto', true),
    col('cardio_id', 'texto', true),
    col('creada_en', 'instante'),
  ],
};

// ---------------------------------------------------------------------------

export const TABS_COMPARTIDAS: DefinicionTab[] = [
  TAB_CUENTAS,
  TAB_GIMNASIOS,
  TAB_EJERCICIOS,
  TAB_MAQUINAS,
];

export const TABS_PERSONALES: DefinicionTab[] = [
  TAB_PERFIL,
  TAB_EJERCICIOS_PROPIOS,
  TAB_PLANES,
  TAB_DIAS_BLOQUEADOS,
  TAB_RUTINAS,
  TAB_RUTINA_DIAS,
  TAB_RUTINA_EJERCICIOS,
  TAB_SESIONES,
  TAB_SERIES,
  TAB_CARDIO,
  TAB_MEDIDAS,
  TAB_COMIDAS,
  TAB_INGESTAS,
];

export const TODAS_LAS_TABS: DefinicionTab[] = [...TABS_COMPARTIDAS, ...TABS_PERSONALES];

export function tabPorNombre(nombre: string): DefinicionTab | undefined {
  return TODAS_LAS_TABS.find((t) => t.nombre === nombre);
}

/** Las pestañas personales que el cliente puede escribir vía sync. */
export const TABS_SINCRONIZABLES: DefinicionTab[] = TABS_PERSONALES;
